import { useCallback, useEffect, useRef, useState } from 'react';
import { callAction } from '../../app/actions';
import { apiRequest, ApiError } from '../../api/client';
import { PresenceStack } from '../presence/PresenceStack';
import { LanguagePicker } from '../../components/LanguagePicker';
import { registerHandle } from '../../app/handles';
import { reportAdminScreen } from '../../app/router';
import { TypedConfirm } from '../../components/TypedConfirm';

/**
 * Live verification targets: the real systems the AI agent may inspect before writing.
 *
 * Three rules here are safety properties, not styling, and the port keeps each one:
 *
 * ROWS SAVE FIELD BY FIELD, on change, rather than through one Save button — so a half-filled
 * target cannot be lost by navigating away. A save deliberately does NOT re-render the table:
 * by the time it resolves the person has usually tabbed on, and redrawing would pull the next
 * field out from under them.
 *
 * CREDENTIALS ARE WRITE-ONLY, like user passwords. The inputs are always blank, only a
 * non-empty value is ever sent, and Save stays disabled until there is something to send — an
 * accidental click must not overwrite a stored credential with an empty one. They are saved by
 * an explicit button and never on blur, because the username and password are ONE sealed blob
 * server-side: a per-field save would send half a credential over the other half.
 *
 * ARMING IS ASYMMETRIC. Turning a production system ON demands typing its name back; turning
 * it OFF is one click. The friction belongs on the dangerous side.
 */

export interface TargetsHandle {
  open: () => void;
  close: () => void;
}

/** How a web target signs in. `db_credentials` is the database kind's only value. */
export type TargetAuthType = 'none' | 'basic' | 'header' | 'form' | 'db_credentials';

/** What the picker offers for a web target, in the order it offers them. */
export const WEB_AUTH_TYPES: { value: TargetAuthType; label: string }[] = [
  { value: 'none', label: 'No sign-in' },
  { value: 'basic', label: 'HTTP basic' },
  { value: 'form', label: 'Sign-in form' },
  { value: 'header', label: 'Header token' },
];

export interface AppTarget {
  id: string;
  name: string;
  kind: 'web' | 'database';
  baseUrl?: string | null;
  /**
   * web + `form`: where the sign-in page is, relative to `baseUrl`. Empty means the start
   * page shows it. Origin- and prefix-locked server-side by `composeUrl()` before any
   * request, exactly like a node's `appPath`.
   */
  loginPath?: string | null;
  /**
   * DECLARED, never inferred (V0.45). It used to be derived from `kind` — every web target
   * got `basic` — so a target whose app signs in through a PAGE was installed with HTTP
   * credentials it never asks for: the agent arrived at the login card, and documented that
   * card as though it were the application.
   */
  authType?: TargetAuthType;
  dbHost?: string | null;
  dbPort?: number | null;
  dbName?: string | null;
  hasCredential?: boolean;
  credentialUpdatedAt?: string | null;
  liveTestingEnabled?: boolean;
  armedAt?: string | null;
  nodeCount?: number;
}

/**
 * The server wants a full URL; people type "portal.example.com".
 *
 * Returns the normalised URL, `null` for empty, or `false` for genuinely unusable — three
 * outcomes rather than two, because "they left it blank" and "that is not an address" deserve
 * different answers.
 */
export function normalizeBaseUrl(v: string | null | undefined): string | null | false {
  const s = (v || '').trim();
  if (!s) return null;
  const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    new URL(withScheme);
    return withScheme;
  } catch {
    return false;
  }
}

const errText = (e: unknown): string =>
  e instanceof ApiError
    ? (e.body as { error?: string } | undefined)?.error || `HTTP ${e.status}`
    : e instanceof Error
      ? e.message
      : 'Unknown error';

/** A field that saves itself when it loses focus, and puts back the old value if that fails. */
function AutoField({
  value,
  placeholder,
  minWidth,
  onSave,
}: {
  value: string;
  placeholder?: string;
  minWidth?: number;
  onSave: (raw: string) => Promise<string | null>;
}) {
  const [text, setText] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => setText(value), [value]);

  return (
    <input
      className="fld"
      style={minWidth ? { minWidth } : undefined}
      placeholder={placeholder}
      value={text}
      disabled={busy}
      onChange={(e) => setText(e.target.value)}
      onBlur={async () => {
        if (text === value) return;
        setBusy(true);
        const settled = await onSave(text);
        setBusy(false);
        // A rejected save puts back what the server still holds, so the field never shows a
        // value that was not stored.
        if (settled !== null) setText(settled);
      }}
    />
  );
}

function CredentialCell({
  target,
  encryptionOk,
  onUpdated,
}: {
  target: AppTarget;
  encryptionOk: boolean;
  onUpdated: (t: AppTarget) => void;
}) {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [saving, setSaving] = useState(false);

  // A web target's DECLARED sign-in mechanism. The default is `basic` rather than `none`
  // because that is what every web target was implicitly set to before this existed, and a
  // row whose server value predates the column must keep reading the same.
  const authType: TargetAuthType =
    target.kind === 'web' ? (target.authType ?? 'basic') : 'db_credentials';

  const setAuthType = async (next: TargetAuthType) => {
    try {
      const updated = await apiRequest<AppTarget>(`/api/ai/targets/${target.id}`, {
        method: 'PUT',
        body: { authType: next },
      });
      onUpdated(updated);
    } catch (e) {
      alert(`Could not change the sign-in type.
${errText(e)}`);
    }
  };

  // Nothing typed means nothing to send. Disabling Save is what stops an accidental click
  // replacing a stored credential with an empty blob.
  const canSave = !!user.trim() || !!password;

  const save = async () => {
    if (!encryptionOk) {
      alert(
        'Credentials cannot be stored yet: APP_TARGET_ENC_KEY is not set on the server.\n\n' +
          'Add it to server/.env and restart the backend.',
      );
      return;
    }
    const credential: Record<string, string> = {};
    if (user.trim()) credential[target.kind === 'web' ? 'username' : 'user'] = user.trim();
    if (password) credential.password = password;
    setSaving(true);
    try {
      const updated = await apiRequest<AppTarget>(`/api/ai/targets/${target.id}`, {
        method: 'PUT',
        // The DECLARED type travels with the credential, so saving one cannot quietly reset
        // a target that was set to 'form' back to 'basic'.
        body: { credential, authType },
      });
      onUpdated(updated);
      setUser('');
      setPassword('');
      setRevealed(false);
    } catch (e) {
      alert(`Could not save credentials.\n${errText(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!confirm(`Remove the stored credentials for "${target.name}"? This also disarms the target.`))
      return;
    try {
      const updated = await apiRequest<AppTarget>(`/api/ai/targets/${target.id}/credential`, {
        method: 'DELETE',
      });
      onUpdated(updated); // the response also reports liveTestingEnabled:false — clearing disarms
    } catch {
      alert('Could not clear credentials.');
    }
  };

  return (
    <div className="tgt-conn">
      {/* Web only: a database target has exactly one mechanism, so a picker offering it a
          choice would be a lie. A <select> and not an <input>, which is also what keeps the
          input indices e2e/26-targets.spec.ts locates by unchanged. */}
      {target.kind === 'web' ? (
        <select
          className="fld"
          aria-label="Sign-in type"
          value={authType}
          onChange={(e) => void setAuthType(e.target.value as TargetAuthType)}
        >
          {WEB_AUTH_TYPES.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
      ) : null}
      <input
        className="fld"
        placeholder={target.kind === 'web' ? 'username' : 'db user'}
        autoComplete="off"
        value={user}
        onChange={(e) => setUser(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSave) void save();
        }}
      />
      <div className="pwd-wrap">
        <input
          className="fld"
          type={revealed ? 'text' : 'password'}
          placeholder={target.hasCredential ? '(unchanged)' : 'password'}
          autoComplete="off"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) void save();
          }}
        />
        <button
          type="button"
          className="pwd-toggle"
          title="Show / hide password"
          onClick={() => setRevealed((r) => !r)}
        >
          <svg className="ico" style={{ width: 16, height: 16 }}>
            <use href={revealed ? '#i-eye-off' : '#i-eye'} />
          </svg>
        </button>
      </div>
      <div className="arm-row">
        <button
          className="pbtn"
          style={{ fontSize: 12, padding: '4px 10px' }}
          disabled={!canSave || saving}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {target.hasCredential ? (
          <button className="pbtn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={clear}>
            Clear
          </button>
        ) : null}
      </div>
      <div className="tgt-meta">
        {target.hasCredential
          ? `Stored${
              target.credentialUpdatedAt
                ? ` · ${new Date(target.credentialUpdatedAt).toLocaleDateString()}`
                : ''
            }`
          : 'None stored'}
      </div>
    </div>
  );
}

function ArmControl({
  target,
  onUpdated,
}: {
  target: AppTarget;
  onUpdated: (t: AppTarget) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  const disarm = async () => {
    if (!confirm(`Disarm "${target.name}"? The agent will no longer be able to contact it.`)) return;
    try {
      onUpdated(await apiRequest<AppTarget>(`/api/ai/targets/${target.id}/disarm`, { method: 'POST' }));
    } catch {
      alert('Could not disarm this target.');
    }
  };

  const arm = async (typed: string) => {
    try {
      onUpdated(
        await apiRequest<AppTarget>(`/api/ai/targets/${target.id}/arm`, {
          method: 'POST',
          body: { confirmName: typed },
        }),
      );
      setConfirming(false);
    } catch (e) {
      alert(`Could not arm this target.\n${errText(e)}`);
    }
  };

  if (target.liveTestingEnabled) {
    return (
      <div>
        <button
          className="pbtn arm-btn armed"
          title={
            target.armedAt
              ? `Armed ${new Date(target.armedAt).toLocaleString()} — click to disarm`
              : 'Click to disarm'
          }
          onClick={disarm}
        >
          ⚠ Armed
        </button>
        {target.armedAt ? (
          <div className="tgt-meta">since {new Date(target.armedAt).toLocaleDateString()}</div>
        ) : null}
      </div>
    );
  }

  if (!confirming) {
    return (
      <div>
        <button className="pbtn arm-btn" onClick={() => setConfirming(true)}>
          Arm live testing
        </button>
      </div>
    );
  }

  // The gesture itself is components/TypedConfirm.tsx since V0.48 — same markup, same classes,
  // same refusal to accept anything but an exact match — because the Backups tab asks for a
  // restore in exactly this way and two copies of a confirmation is how they drift apart.
  return (
    <div>
      <TypedConfirm
        prompt={
          <>
            This lets the AI agent interact with the <b>real</b> system. Type <b>{target.name}</b>{' '}
            to confirm.
          </>
        }
        expected={target.name}
        actionLabel="Arm"
        onConfirm={arm}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

function AddTargetRow({ onCreated }: { onCreated: (t: AppTarget) => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'web' | 'database'>('web');
  const [conn, setConn] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const connRef = useRef<HTMLInputElement>(null);

  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      nameRef.current?.focus();
      return;
    }
    const body: Record<string, unknown> = { name: trimmed, kind };
    const c = conn.trim();
    if (c) {
      if (kind === 'web') {
        const u = normalizeBaseUrl(c);
        if (u === false) {
          alert('That doesn’t look like a valid address.');
          connRef.current?.focus();
          return;
        }
        body.baseUrl = u;
      } else {
        body.dbHost = c;
      }
    }
    try {
      onCreated(await apiRequest<AppTarget>('/api/ai/targets', { method: 'POST', body }));
      setName('');
      setConn('');
    } catch (e) {
      alert(`Could not create this target.\n${errText(e)}`);
    }
  };

  return (
    <tr>
      <td>
        <input
          ref={nameRef}
          className="fld"
          style={{ minWidth: 140 }}
          placeholder="New target name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </td>
      <td>
        <select
          className="fld"
          value={kind}
          onChange={(e) => setKind(e.target.value as 'web' | 'database')}
        >
          <option value="web">Web app</option>
          <option value="database">Database</option>
        </select>
      </td>
      <td>
        <input
          ref={connRef}
          className="fld"
          placeholder={kind === 'web' ? 'https://app.example.com' : 'host'}
          value={conn}
          onChange={(e) => setConn(e.target.value)}
        />
      </td>
      <td />
      <td />
      <td />
      <td>
        <button className="icobtn" title="Add target" onClick={add}>
          <svg className="ico">
            <use href="#i-plus" />
          </svg>
        </button>
      </td>
    </tr>
  );
}

export function TargetsScreen({ host }: { host: HTMLElement }) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<AppTarget[]>([]);
  const [encryptionOk, setEncryptionOk] = useState(true);

  useEffect(() => {
    host.classList.toggle('show', open);
  }, [open, host]);

  // The URL's /admin/targets segment (plan 1.6): reported from an effect, idempotently,
  // because the screen's internal close paths set `open` directly.
  useEffect(() => {
    reportAdminScreen('targets', open);
  }, [open]);

  const api = useRef<TargetsHandle>({ open: () => {}, close: () => {} });
  api.current.open = useCallback(() => setOpen(true), []);
  api.current.close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    registerHandle('targets', api.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    let live = true;
    void apiRequest<{ targets?: AppTarget[]; encryptionConfigured?: boolean }>(
      '/api/ai/targets',
    ).then(
      (d) => {
        if (!live) return;
        setTargets(d.targets || []);
        setEncryptionOk(d.encryptionConfigured !== false);
        // The node popup offers these in a dropdown and is still vanilla, so it reads the
        // list from the script's own cache. Keep the two in step from here.
        callAction('setAppTargets', d.targets || [], d.encryptionConfigured !== false);
      },
      () => {
        /* leave whatever is shown; the screen is still usable for what is loaded */
      },
    );
    return () => {
      live = false;
    };
  }, [open]);

  const replace = useCallback((t: AppTarget) => {
    setTargets((prev) => {
      const next = prev.map((x) => (x.id === t.id ? { ...x, ...t } : x));
      callAction('setAppTargets', next, true);
      return next;
    });
  }, []);

  /** Save one field. Returns what the field should now show, or null to leave it alone. */
  const saveField = useCallback(
    async (t: AppTarget, field: string, value: unknown, fallback: string): Promise<string | null> => {
      try {
        const updated = await apiRequest<AppTarget>(`/api/ai/targets/${t.id}`, {
          method: 'PUT',
          body: { [field]: value },
        });
        // Deliberately no re-render of the table here: by the time this resolves the person
        // has usually tabbed on, and redrawing would pull the next field out from under them.
        setTargets((prev) => {
          const next = prev.map((x) => (x.id === t.id ? { ...x, ...updated } : x));
          callAction('setAppTargets', next, true);
          return next;
        });
        return null;
      } catch (e) {
        alert(`Could not save.\n${errText(e)}`);
        return fallback;
      }
    },
    [],
  );

  const remove = async (t: AppTarget) => {
    const extra = t.nodeCount
      ? `\n\n${t.nodeCount} node${t.nodeCount === 1 ? '' : 's'} point at it; they will simply lose the mapping (no nodes are deleted).`
      : '';
    if (!confirm(`Delete the target "${t.name}"?${extra}`)) return;
    try {
      await apiRequest(`/api/ai/targets/${t.id}`, { method: 'DELETE' });
      const next = targets.filter((x) => x.id !== t.id);
      setTargets(next);
      // Nodes were un-mapped server-side (onDelete: SetNull); mirror it locally so an open
      // popup cannot offer a target that no longer exists.
      callAction('unmapDeletedTarget', t.id, next);
    } catch (e) {
      alert(`Could not delete this target.\n${errText(e)}`);
    }
  };

  if (!open) return null;

  const close = () => {
    setOpen(false);
  };

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-server" />
          </svg>
          Live verification targets
        </h2>
        <PresenceStack />

        <LanguagePicker title="Language" />

        {/* INERT, and deliberately so: #targets-info has no handler in infostation.html
            either, and there is no 'targets' entry in i18n/help.ts for it to show. Porting it
            with a handler would be adding a feature under cover of a refactor; dropping it
            would be removing one. It is carried across exactly as it behaves today, and
            reported separately. */}
        <button className="btn" id="targets-info">
          <svg className="ico">
            <use href="#i-info" />
          </svg>
          <span>Information</span>
        </button>
        <button
          className="btn"
          id="targets-to-generate"
          onClick={() => {
            close();
            callAction('openAiDocModal');
          }}
        >
          <svg className="ico">
            <use href="#i-ai" />
          </svg>
          <span>Generate documentation</span>
        </button>
        <button
          className="btn"
          id="targets-to-config"
          onClick={() => {
            close();
            callAction('openAiDocConfig');
          }}
        >
          <svg className="ico">
            <use href="#i-ai" />
          </svg>
          <span>AI context</span>
        </button>
        <button className="btn" id="targets-close" onClick={close}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Back to InfoStation</span>
        </button>
      </div>

      <div className="perm-wrap">
        <div className="perm-inner">
          {/* Without the key the server cannot store a credential at all. Saying so up front
              beats letting somebody type one in and be refused. */}
          {!encryptionOk ? (
            <div id="targets-warn" className="targets-warn">
              <b>Credential storage is not configured.</b> Set <code>APP_TARGET_ENC_KEY</code> in the
              server’s <code>.env</code> (generate one with <code>openssl rand -base64 32</code>) and
              restart it. Until then, targets can be created but credentials cannot be saved.
            </div>
          ) : null}

          <div className="perm-section-lead">
            A target is a real system that part of the map documents — a web application or a
            database. Point nodes at a target, and the AI agent can explore the real thing before
            writing their documentation. Targets are <b>disarmed</b> until you explicitly arm them;
            nothing is ever contacted while disarmed.
          </div>

          <div className="card">
            <table className="utable" id="targets-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Connection</th>
                  <th>Credentials</th>
                  <th>Live testing</th>
                  <th>Nodes</th>
                  <th />
                </tr>
              </thead>
              <tbody id="targets-tbody">
                {targets.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <AutoField
                        value={t.name}
                        minWidth={140}
                        onSave={async (raw) => {
                          const v = raw.trim();
                          if (!v) return t.name; // a target with no name is not a rename
                          return saveField(t, 'name', v, t.name);
                        }}
                      />
                    </td>
                    {/* Create-time only: changing the kind would strand the connection fields. */}
                    <td>
                      <span style={{ fontSize: 12.5, color: 'var(--ink-dim)' }}>
                        {t.kind === 'web' ? 'Web app' : 'Database'}
                      </span>
                    </td>
                    <td>
                      <div className="tgt-conn">
                        {t.kind === 'web' ? (
                          <AutoField
                            value={t.baseUrl || ''}
                            placeholder="https://app.example.com"
                            onSave={async (raw) => {
                              const v = normalizeBaseUrl(raw);
                              if (v === false) {
                                alert('That doesn’t look like a valid address.');
                                return t.baseUrl || '';
                              }
                              const settled = await saveField(t, 'baseUrl', v, t.baseUrl || '');
                              // Show the normalised form back, so what is on screen is what
                              // was stored.
                              return settled === null ? v || '' : settled;
                            }}
                          />
                        ) : null}
                        {/* AFTER baseUrl, so `.tgt-conn input` index 1 is still baseUrl for
                            e2e/26-targets. Only meaningful for a 'form' target, but drawn for
                            every web one: it is where you say the sign-in page is, and hiding
                            it behind the picker would make the picker's own effect invisible.
                            Relative only — the server refuses an absolute path, because that
                            would be a way past the origin lock. */}
                        {t.kind === 'web' ? (
                          <AutoField
                            value={t.loginPath || ''}
                            placeholder="/login — sign-in form path"
                            onSave={(raw) =>
                              saveField(t, 'loginPath', raw.trim() || null, t.loginPath || '')
                            }
                          />
                        ) : null}
                        {t.kind === 'database' ? (
                          <>
                            <AutoField
                              value={t.dbHost || ''}
                              placeholder="host"
                              onSave={(raw) =>
                                saveField(t, 'dbHost', raw.trim() || null, t.dbHost || '')
                              }
                            />
                            <AutoField
                              value={t.dbPort == null ? '' : String(t.dbPort)}
                              placeholder="3306"
                              onSave={(raw) =>
                                saveField(
                                  t,
                                  'dbPort',
                                  raw.trim() ? parseInt(raw.trim(), 10) : null,
                                  t.dbPort == null ? '' : String(t.dbPort),
                                )
                              }
                            />
                            <AutoField
                              value={t.dbName || ''}
                              placeholder="database"
                              onSave={(raw) =>
                                saveField(t, 'dbName', raw.trim() || null, t.dbName || '')
                              }
                            />
                          </>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <CredentialCell target={t} encryptionOk={encryptionOk} onUpdated={replace} />
                    </td>
                    <td>
                      <ArmControl target={t} onUpdated={replace} />
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--ink-dim)' }}>
                      {t.nodeCount === 1 ? '1 node' : `${t.nodeCount ?? 0} nodes`}
                    </td>
                    <td>
                      <button className="icobtn" title="Delete target" onClick={() => remove(t)}>
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
                <AddTargetRow
                  onCreated={(t) =>
                    setTargets((prev) => {
                      const next = [...prev, t].sort((a, b) => a.name.localeCompare(b.name));
                      callAction('setAppTargets', next, true);
                      return next;
                    })
                  }
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
